import { RestClientRequestController } from '../RestClientRequestController/RestClientRequestController';
import { IRestClientRequestOptions } from '../RestClientRequestController/RestClientRequestController.types';
import {
  TRequestInterceptor,
  TResponseInterceptor,
  IRestClientParams,
  IRestClientRequestMethodOptions,
  TResponseInterceptorFulfillFn,
  TResponseInterceptorRejectFn,
  ICreateSubApiClientParams,
  IRestClientRequestGetMethodOptions,
} from './RestClient.types';
import { uriQueryParamsToString } from './RestClient.utils';

export class RestClient {
  private requestController: RestClientRequestController;
  private requestInterceptors: Set<TRequestInterceptor> = new Set();
  private responseInterceptors: Set<TResponseInterceptor> = new Set();
  private baseUrl: string;
  private apiVersion: string | null;

  constructor(params: IRestClientParams) {
    const { baseUrl = '', headers, apiVersion } = params;
    this.baseUrl = baseUrl;
    this.apiVersion = apiVersion || null;
    this.requestController = new RestClientRequestController({
      headers,
    });
  }

  addRequestInterceptor(interceptor: TRequestInterceptor): () => void {
    this.requestInterceptors.add(interceptor);
    return () => {
      this.requestInterceptors.delete(interceptor);
    };
  }

  addResponseInterceptor<D>(
    onFulfilled?: TResponseInterceptorFulfillFn<D>,
    onRejected?: TResponseInterceptorRejectFn
  ): () => void {
    const interceptor = {
      onFulfilled,
      onRejected,
    };
    this.responseInterceptors.add(interceptor);
    return () => {
      this.responseInterceptors.delete(interceptor);
    };
  }

  abortAllRequests(): void {
    this.requestController.abortAllRequests();
  }

  async request<D, B = any>(
    endpoint: string,
    options: IRestClientRequestOptions<B>,
    requestName = ''
  ): Promise<D> {
    let modifiedOptions: IRestClientRequestOptions<B> = options;

    const baseUrlNamePart = options.baseUrl?.split('/').pop();
    const processedRequestName =
      requestName || [baseUrlNamePart, this.apiVersion, endpoint].filter(Boolean).join('.');

    for (const interceptor of this.requestInterceptors) {
      modifiedOptions = await interceptor(modifiedOptions, processedRequestName);
      if (modifiedOptions.baseUrl !== options.baseUrl && modifiedOptions.baseUrl !== undefined) {
        this.baseUrl = modifiedOptions.baseUrl;
      }
    }

    modifiedOptions = {
      ...modifiedOptions,
      baseUrl: options.baseUrl ?? [this.baseUrl, this.apiVersion].filter(Boolean).join('/'),
    };

    try {
      const response = await this.requestController.makeRequest<D, B>(endpoint, modifiedOptions);
      let modifiedResponse = response.data;
      for (const interceptor of this.responseInterceptors) {
        modifiedResponse =
          interceptor.onFulfilled?.(
            { data: modifiedResponse, headers: response.headers },
            processedRequestName
          ) || modifiedResponse;
      }
      return modifiedResponse;
    } catch (error) {
      let modifiedError = error;
      for (const interceptor of this.responseInterceptors) {
        modifiedError =
          interceptor.onRejected?.(modifiedError, processedRequestName) || modifiedError;
      }
      if (!(modifiedError instanceof Error)) {
        return modifiedError;
      }
      throw modifiedError;
    }
  }

  post<D, B = any>(
    endpoint: string,
    options?: IRestClientRequestMethodOptions<B>,
    requestName?: string
  ): Promise<D> {
    const query = uriQueryParamsToString(options?.queryParams);
    return this.request<D, B>(`${endpoint}${query}`, { ...options, method: 'POST' }, requestName);
  }

  get<D, B = any>(
    endpoint: string,
    options?: IRestClientRequestGetMethodOptions<B>,
    requestName?: string
  ): Promise<D> {
    const query = uriQueryParamsToString(options?.queryParams);
    return this.request<D, B>(`${endpoint}${query}`, { ...options, method: 'GET' }, requestName);
  }

  put<D, B = any>(
    endpoint: string,
    options?: IRestClientRequestMethodOptions<B>,
    requestName?: string
  ): Promise<D> {
    return this.request<D, B>(endpoint, { ...options, method: 'PUT' }, requestName);
  }
  patch<D, B = any>(
    endpoint: string,
    options?: IRestClientRequestMethodOptions<B>,
    requestName?: string
  ): Promise<D> {
    return this.request<D, B>(endpoint, { ...options, method: 'PATCH' }, requestName);
  }

  delete<D, B = any>(
    endpoint: string,
    options?: IRestClientRequestMethodOptions<B>,
    requestName?: string
  ): Promise<D> {
    return this.request<D, B>(endpoint, { ...options, method: 'DELETE' }, requestName);
  }

  createSubApiClient(params: ICreateSubApiClientParams): RestClient {
    return new Proxy(this, {
      get(target, prop, receiver): any {
        const baseUrl = [params.baseUrl ?? target.baseUrl, params.rootApi]
          .filter(Boolean)
          .join('/');
        const apiVersion = params.apiVersion ?? target.apiVersion;
        const value = Reflect.get(target, prop, receiver);

        if (prop === 'baseUrl') {
          return baseUrl;
        }

        if (prop === 'apiVersion') {
          return apiVersion;
        }

        const originalRequestMethod = target[prop as keyof RestClient];
        if (prop === 'request' && typeof originalRequestMethod === 'function') {
          return (
            ...args: Parameters<RestClient['request']>
          ): ReturnType<RestClient['request']> => {
            const [endpoint, options, requestName] = args;
            const newRequestName = [params.rootApi, apiVersion, requestName || endpoint]
              .filter(Boolean)
              .join('.');
            return originalRequestMethod.apply(receiver, [endpoint, options, newRequestName]);
          };
        }

        return value;
      },
    });
  }
}
